import { createClient } from '@supabase/supabase-js';

// Access Vite environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
const hasSupabaseConfig = Boolean(
  supabaseUrl &&
  supabaseAnonKey &&
  !supabaseUrl.includes('placeholder')
);

// Demo mode is allowed only during non-production work. Production must fail closed.
export const isSupabaseMisconfigured = !hasSupabaseConfig && import.meta.env.PROD;
export const isMockMode = !hasSupabaseConfig && !import.meta.env.PROD;
export const supabaseConfigErrorMessage =
  'Authentication is temporarily unavailable because the app is missing its Supabase configuration.';

console.log('🔍 Supabase Configuration Check:');
console.log('  - VITE_SUPABASE_URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
console.log('  - VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Set' : '❌ Missing');
console.log(
  '  - auth mode:',
  isSupabaseMisconfigured ? '❌ MISCONFIGURED (PRODUCTION BLOCKED)' : isMockMode ? '⚠️ MOCK MODE' : '✅ LIVE MODE'
);

if (isSupabaseMisconfigured) {
  console.error(`⚠️  ${supabaseConfigErrorMessage}`);
} else if (!hasSupabaseConfig) {
  console.warn(
    '⚠️  Supabase credentials are missing. The app will run in MOCK MODE.'
  );
}

// Fallback to empty strings to prevent crash
export const supabase = createClient(
  hasSupabaseConfig ? supabaseUrl! : 'https://placeholder.supabase.co',
  hasSupabaseConfig ? supabaseAnonKey! : 'placeholder'
);

// Cache for organisation ID to reduce database queries
let cachedOrgId: string | null = null;
let cachedUserId: string | null = null;

const isInvalidSessionError = (err: any) => {
  const msg = err?.message || err?.error_description || err?.details || '';
  const status = err?.status;
  return (
    status === 401 ||
    status === 403 ||
    msg.includes('invalid claim') ||
    msg.includes('missing sub claim') ||
    msg.toLowerCase().includes('jwt') ||
    msg.toLowerCase().includes('invalid token') ||
    msg.toLowerCase().includes('session expired')
  );
};

const clearSupabaseAuthStorage = () => {
  try {
    // Supabase typically stores auth under keys like `sb-<project-ref>-auth-token`.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') && key.includes('auth-token')) localStorage.removeItem(key);
    }
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (key.startsWith('sb-') && key.includes('auth-token')) sessionStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
};

export const clearInvalidSession = async () => {
  clearSupabaseAuthStorage();
  clearOrgCache();
  try {
    // Use local sign-out to avoid network calls (which can loop on 401/403).
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // ignore
  }
};

// Helper to get the current organisation ID
export const getCurrentOrganisationId = async (): Promise<string> => {
  if (isMockMode) return 'mock-org-id';

  try {
    // Use local session first (avoids /auth/v1/user network call, which can 403-loop on expired tokens)
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      if (isInvalidSessionError(sessionError)) {
        console.warn('Invalid session detected in getCurrentOrganisationId, clearing...');
        await clearInvalidSession();
        throw new Error('Session expired. Please sign in again.');
      }
      throw sessionError;
    }

    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    // Return cached org ID if user hasn't changed
    if (cachedOrgId && cachedUserId === user.id) {
      return cachedOrgId;
    }

    // Query profiles table for organisation_id (be tolerant of missing rows)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('organisation_id')
      .eq('id', user.id)
      .limit(1)
      .maybeSingle();

    if (profileError && isInvalidSessionError(profileError)) {
      console.warn('Invalid session detected in profile query, clearing...');
      await clearInvalidSession();
      throw new Error('Session expired. Please sign in again.');
    }

    // If profile is missing or not linked yet, auto-provision an org (security definer RPC)
    if (profileError || !profile?.organisation_id) {
      const { data: ensuredOrgId, error: ensureError } = await supabase.rpc('ensure_user_has_org');
      if (ensureError) {
        if (isInvalidSessionError(ensureError)) {
          console.warn('Invalid session detected in ensure_user_has_org, clearing...');
          await clearInvalidSession();
          throw new Error('Session expired. Please sign in again.');
        }
        throw ensureError;
      }
      if (!ensuredOrgId) {
        // Preserve the original error context if available
        if (profileError) throw profileError;
        throw new Error('Failed to provision organisation');
      }
      cachedOrgId = ensuredOrgId;
      cachedUserId = user.id;
      return ensuredOrgId;
    }

    // Cache the result
    cachedOrgId = profile.organisation_id;
    cachedUserId = user.id;

    return profile.organisation_id;
  } catch (error: any) {
    if (isInvalidSessionError(error)) {
      console.warn('Invalid session detected in getCurrentOrganisationId catch, clearing...');
      await clearInvalidSession();
      throw new Error('Session expired. Please sign in again.');
    }

    console.error('Failed to get organisation ID:', error);
    throw error;
  }
};

// Clear cache on logout
export const clearOrgCache = () => {
  cachedOrgId = null;
  cachedUserId = null;
};

// Centralized error message extractor
export const getErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error?.message) return error.message;
  if (error?.error_description) return error.error_description;
  if (error?.details) return error.details;
  
  try {
    const stringified = JSON.stringify(error);
    if (stringified !== '{}' && stringified.length < 200) {
       return stringified;
    }
  } catch {
    // ignore
  }
  
  return 'An unexpected error occurred';
};
